import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  contentChildren,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  model,
  signal,
  untracked,
  viewChild,
  viewChildren,
  booleanAttribute,
} from '@angular/core';

import { NgpTabButton, NgpTabList, NgpTabPanel, NgpTabset } from 'ng-primitives/tabs';
import { injectDisposables } from 'ng-primitives/utils';

import { I18nPipe } from '@core/i18n';

import { IconComponent } from '@shared/icon';
import { twMerge } from '@shared/tw-merge';

import { StepTabEditDescriptionDefDirective, TabPanelDirective } from './tab-group.directive';
import type { SlidingRect, StepTabOption, StepTabStatus } from './tab-group.types';
import {
  slidingBarVariants,
  stepTabButtonVariants,
  stepTabLabelVariants,
  stepTabConnectorVariants,
  stepTabDescriptionVariants,
  stepTabListVariants,
  stepTabMarkerVariants,
  tabPanelVariants,
} from './tab-group.variants';

@Component({
  selector: 'ks-step-tab-group',
  imports: [
    NgpTabset,
    NgpTabList,
    NgpTabButton,
    NgpTabPanel,
    NgTemplateOutlet,
    IconComponent,
    I18nPipe,
  ],
  host: { '[class]': 'hostClass()' },
  templateUrl: './step-tab-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepTabGroupComponent {
  readonly options = input.required<readonly StepTabOption[]>();
  readonly clickable = input(false, { transform: booleanAttribute });
  readonly extraClass = input<string>('', { alias: 'class' });

  readonly value = model<string | undefined>(undefined);

  private readonly tabset = viewChild.required(NgpTabset);
  private readonly itemRefs = viewChildren<ElementRef<HTMLElement>>('item');

  protected readonly panels = contentChildren(TabPanelDirective);
  protected readonly editDescriptionDef = contentChild(StepTabEditDescriptionDefDirective);

  private readonly disposables = injectDisposables();
  private readonly hostRef = inject(ElementRef<HTMLElement>);

  private cancelRectUpdate: (() => void) | null = null;

  protected readonly rect = signal<SlidingRect | null>(null);
  protected readonly rectAnimated = signal(false);

  protected readonly stepTabButtonClass = stepTabButtonVariants;
  protected readonly stepTabLabelClass = stepTabLabelVariants;
  protected readonly stepTabConnectorClass = stepTabConnectorVariants;
  protected readonly stepTabDescriptionClass = stepTabDescriptionVariants;
  protected readonly stepTabListClass = stepTabListVariants;
  protected readonly stepTabMarkerClass = stepTabMarkerVariants;
  protected readonly stepTabPanelClass = tabPanelVariants;

  protected readonly hostClass = computed(() => twMerge('w-full', this.extraClass()));
  protected readonly activeStatus = computed(() => {
    const value = this.value();
    const option = this.options().find((opt) => opt.value === value);

    return option?.status ?? 'pending';
  });
  protected readonly slidingBarClass = computed(() =>
    twMerge(
      slidingBarVariants({ orientation: 'horizontal', side: 'bottom' }),
      this.activeStatus() === 'error'
        ? 'bg-danger-600'
        : this.activeStatus() === 'success'
          ? 'bg-success-600'
          : 'bg-primary-600',
      !this.rectAnimated() && 'transition-none',
    ),
  );

  constructor() {
    effect(() => {
      const options = this.options();
      const value = this.value();
      const valid = options.some((opt) => opt.value === value && !opt.disabled);

      if (!valid) {
        const firstEnabled = options.find((opt) => !opt.disabled);

        this.value.set(firstEnabled?.value);
      }
    });

    effect(() => {
      const options = this.options();
      const value = this.value();
      const refs = this.itemRefs();

      if (!refs.length) return;

      untracked(() => this.updateRect(value, refs, options));
    });

    const observer = new ResizeObserver(() =>
      untracked(() => this.updateRect(this.value(), this.itemRefs(), this.options())),
    );

    observer.observe(this.hostRef.nativeElement as Element);
    inject(DestroyRef).onDestroy(() => observer.disconnect());
  }

  sync(): void {
    const value = this.value();

    if (value === undefined) return;

    this.tabset().select(value);
  }

  protected statusOf(option: StepTabOption): StepTabStatus {
    return option.status ?? 'pending';
  }

  protected onValueChange(value: string | undefined): void {
    if (value === undefined) return;

    this.value.set(value);
  }

  private updateRect(
    value: string | undefined,
    refs: readonly ElementRef<HTMLElement>[],
    options: readonly StepTabOption[],
  ): void {
    this.cancelRectUpdate?.();
    this.cancelRectUpdate = null;

    if (value === undefined) {
      this.rect.set(null);

      return;
    }

    const index = options.findIndex((opt) => opt.value === value);

    if (index === -1 || refs[index] === undefined) {
      this.rect.set(null);

      return;
    }

    this.cancelRectUpdate = this.disposables.requestAnimationFrame(() => {
      const itemElement = refs[index]?.nativeElement;

      if (!itemElement) return;

      this.rect.set({
        top: itemElement.offsetTop,
        left: itemElement.offsetLeft,
        width: itemElement.offsetWidth,
        height: itemElement.offsetHeight,
      });

      if (this.rectAnimated()) return;

      this.disposables.requestAnimationFrame(() => this.rectAnimated.set(true));
    });
  }
}
